import { useEffect, useRef, useState } from 'react';

/** A pane reopened within this delay of being closed comes back on the conversation it showed; later, on the home screen. */
export const RESUME_WINDOW_MS = 5 * 60_000;
/** How often an open pane stamps itself: Office does not reliably fire `pagehide` when the pane is closed. */
export const HEARTBEAT_MS = 30_000;
const KEY = 'agent_v2_open_thread';

/** One record per workbook: panes of several open workbooks do not overwrite each other. */
type OpenThreads = Record<string, { threadId: string | null; at: number }>;

function readAll(): OpenThreads {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? 'null') as OpenThreads | null;
    return stored && typeof stored === 'object' ? stored : {};
  } catch {
    return {};
  }
}

export function rememberOpenThread(workbookId: string, threadId: string | null, now: number): void {
  // Drop the expired records on the way, so the map only holds the workbooks seen in the last minutes.
  const kept = Object.fromEntries(Object.entries(readAll()).filter(([, r]) => now - r.at < RESUME_WINDOW_MS));
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...kept, [workbookId]: { threadId, at: now } } satisfies OpenThreads));
  } catch {
    /* storage blocked: the pane opens on the home screen next time */
  }
}

/** The thread this workbook's pane showed when it was last seen alive, if that was less than `RESUME_WINDOW_MS` ago. */
export function recentThread(workbookId: string, now: number): string | null {
  const record = readAll()[workbookId];
  return record?.threadId && now - record.at < RESUME_WINDOW_MS ? record.threadId : null;
}

/**
 * Hybrid reopen: once the workbook is known (and its identity answered), reopen the thread the pane showed if it was
 * closed less than five minutes ago, otherwise stay on the home screen. From then on, keep stamping the open thread
 * (a new thread stamps `null`, so a reopen lands on the home screen). The stamping starts only after that decision,
 * or the empty pane would overwrite the record before it is read.
 *
 * Returns whether a thread is about to be reopened (from the moment the workbook is known until it is loaded), so
 * the pane can show nothing rather than flash the home screen.
 */
export function useRecentThread({ workbookId, ready, threadId, load }: { workbookId: string | null; ready: boolean; threadId: string | null; load: (threadId: string) => Promise<void> }): boolean {
  const [decided, setDecided] = useState(false);
  const deciding = useRef(false);
  const current = useRef(threadId);
  current.current = threadId;

  useEffect(() => {
    if (!workbookId || !ready || deciding.current) return;
    deciding.current = true;
    const recent = recentThread(workbookId, Date.now());
    // Only into an empty pane: the user may already have opened something.
    if (!recent || current.current) {
      setDecided(true);
      return;
    }
    void load(recent)
      .catch((err) => console.warn('[agent-v2] resume failed', err))
      .finally(() => setDecided(true));
  }, [workbookId, ready, load]);

  useEffect(() => {
    if (!decided || !workbookId) return;
    const stamp = () => rememberOpenThread(workbookId, current.current, Date.now());
    stamp();
    const timer = setInterval(stamp, HEARTBEAT_MS);
    window.addEventListener('pagehide', stamp);
    document.addEventListener('visibilitychange', stamp);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', stamp);
      document.removeEventListener('visibilitychange', stamp);
    };
  }, [decided, workbookId, threadId]);

  return !decided && !!workbookId && !threadId && recentThread(workbookId, Date.now()) !== null;
}
